import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { usePaletteGenerator } from '../hooks/usePaletteGenerator';
import BrandSelector from '../components/BrandSelector';
import FilterSidebar from '../components/FilterSidebar';
import { Stars, Share, Dices, TriangleAlert, LayoutGrid, List, Palette, Search, FolderHeart, Heart, Download, ChevronLeft, ChevronRight, ChevronDown, Check } from 'lucide-react';

import PaletteGrid from '../components/PaletteGrid';

import SharePaletteModal from '../components/SharePaletteModal';
import SavePaletteModal from '../components/SavePaletteModal';
import LoadPaletteModal from '../components/LoadPaletteModal';
import ImportPaletteModal from '../components/ImportPaletteModal';
import MarkerSearchModal from '../components/MarkerSearchModal';
import PageHelpModal from '../components/PageHelpModal';
import { encodePaletteForShare, decodePaletteFromShare } from '../utils/paletteCode';
import { useI18n } from '../contexts/I18nContext';
import { getCollectionMarkerCount, getUserCollection } from '../utils/userCollection';
import { OHUHU_SET_LEGEND, ARRTX_SET_LEGEND, SFAIH_SET_LEGEND, CALIART_SETS, BIANYO_SETS, HOTU_SETS, CALM_OVER_CHAOS_SETS, HANGFAN_SETS, SANJOKI_SET_LEGEND, PRISM_SETS, ROYAL_LANGNICKEL_SETS, COPIC_SKETCH_SETS, COPIC_CLASSIC_SETS, COPIC_CIAO_SETS } from '../utils/data';

const CountSelect = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative min-w-[80px]" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        {value}
        <ChevronDown size={13} className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-24 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-48 overflow-y-auto">
          {Array.from({ length: 18 }, (_, i) => i + 3).map(n => (
            <button
              key={n}
              type="button"
              onClick={() => { onChange(n); setOpen(false); }}
              className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${value === n ? 'text-rose-pastel-600 font-semibold' : 'text-slate-700'}`}
            >
              {n}
              {value === n && <Check size={12} className="text-rose-pastel-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SetSelect = ({ value, onChange, legend, ariaLabel }) => (
  <div className="relative mt-1.5">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full appearance-none pr-8 text-xs py-2 px-3 cursor-pointer rounded-xl bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      aria-label={ariaLabel}
    >
      <option value="All">All Sets</option>
      {Object.entries(legend).map(([key, label]) => (
        <option key={key} value={key}>{label}</option>
      ))}
    </select>
    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
      <svg className="w-3 h-3 text-rose-pastel-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

export default function RandomPalettePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { brand, setBrand, ohuhuMode, setOhuhuMode } = useAppContext();
  const { t } = useI18n();
  const [collectionCount, setCollectionCount] = useState(() =>
    brand === "My Collection" ? getCollectionMarkerCount() : 0
  );

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isLoadOpen, setIsLoadOpen] = useState(false);
  const [importMarkers, setImportMarkers] = useState(null);
  const [isCompactMode, setIsCompactMode] = useState(true);
  const [ohuhuSet, setOhuhuSet] = useState('All');
  const [arrtxSet, setArrtxSet] = useState('All');
  const [sfaihSet, setSfaihSet] = useState('All');
  const [caliartSet, setCaliartSet] = useState('All');
  const [bianyoSet, setBianyoSet] = useState('All');
  const [hotuSet, setHotuSet] = useState('All');
  const [calmOverChaosSet, setCalmOverChaosSet] = useState('All');
  const [hangfanSet, setHangfanSet] = useState('All');
  const [sanjokiSet, setSanjokiSet] = useState('All');
  const [prismSet, setPrismSet] = useState('All');
  const [royalLangnickelSet, setRoyalLangnickelSet] = useState('All');
  const [copicSketchSet, setCopicSketchSet] = useState('All');
  const [copicClassicSet, setCopicClassicSet] = useState('All');
  const [copicCiaoSet, setCopicCiaoSet] = useState('All');
  const [collectionBrand, setCollectionBrand] = useState('All');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [replacementColor, setReplacementColor] = useState(null);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const paletteRef = useRef(null);
  const generateMobileRef = useRef(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const filterScrollLockRef = useRef({
    bodyOverflow: '',
    bodyPaddingRight: '',
    bodyPosition: '',
    bodyTop: '',
    bodyWidth: '',
    bodyTouchAction: '',
    htmlOverflow: '',
    scrollY: 0,
  });

  const {
    palette,
    count,
    filter,
    similarSubfilter,
    locks,
    regeneratePalette,
    toggleLock,
    setCountClamped,
    setFilterAndRegenerate,
    setSimilarSubfilterAndRegenerate,
    isSimilarMode,
    goToPreviousPalette,
    hasPreviousPalette,
    goToNextPalette,
    hasNextPalette,
    replaceColorAt,
    loadPalette
  } = usePaletteGenerator({
    initialCount: 12,
    initialBrand: brand,
    initialFilter: 'Random'
  });

  // Track if initial palette has been generated
  const initialLoadRef = useRef(false);
  // Track the previous brand to detect actual changes vs initial mount
  const prevBrandRef = useRef(brand);
  // Track if we just loaded from URL code (to skip brand effect once)
  const justLoadedFromCodeRef = useRef(false);

  // Get code from URL search params (React Router)
  const urlCode = searchParams.get('code');

  // Update collection count on mount/navigation (in case user added markers on another page)
  useEffect(() => {
    if (brand === "My Collection") {
      setCollectionCount(getCollectionMarkerCount());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate initial palette or load from URL
  useEffect(() => {
    if (urlCode) {
      try {
        const decodedPalette = decodePaletteFromShare(urlCode);
        if (decodedPalette && decodedPalette.length > 0) {
          loadPalette(decodedPalette);
          initialLoadRef.current = true;
          justLoadedFromCodeRef.current = true; // Flag to skip brand effect
          return; // Don't generate random palette if we loaded from code
        }
      } catch (err) {
        console.error('Failed to load palette from URL:', err);
        // Fall through to generate random palette
      }
    }

    // Only generate initial palette once
    if (!initialLoadRef.current) {
      regeneratePalette();
      initialLoadRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode]); // Watch for URL code changes via React Router

  // Regenerate when brand changes (but not when we just loaded from URL code)
  useEffect(() => {
    // Skip if we just loaded a palette from URL code
    if (justLoadedFromCodeRef.current) {
      justLoadedFromCodeRef.current = false;
      prevBrandRef.current = brand;
      return;
    }

    // Skip on initial mount (brand hasn't changed yet)
    const brandChanged = prevBrandRef.current !== brand;
    prevBrandRef.current = brand;

    if (!brandChanged && !initialLoadRef.current) {
      return;
    }

    // Reset ohuhuSet when switching away from Ohuhu brands
    const isOhuhuBrand = brand === "Ohuhu" || brand === "Ohuhu (New)" || brand === "Ohuhu (Old)";
    const isArrtxBrand = brand === "Arrtx";
    const isSfaihBrand = brand === "SFAIH";
    const isCaliartBrand = brand === "Caliart";
    const isBianyoBrand = brand === "Bianyo";
    const isHotuBrand = brand === "Hotu";
    const isCalmOverChaosBrand = brand === "Calm Over Chaos";
    const isHangfanBrand = brand === "Hangfan";
    const isSanjokiBrand = brand === "Sanjoki";
    const isPrismBrand = brand === "Prism";
    const isRoyalLangnickelBrand = brand === "Royal & Langnickel";
    const isCopicSketchBrand = brand === "Copic - Sketch";
    const isCopicClassicBrand = brand === "Copic - Classic";
    const isCopicCiaoBrand = brand === "Copic - Ciao";
    const isMyCollection = brand === "My Collection";
    if (!isOhuhuBrand) setOhuhuSet('All');
    if (!isArrtxBrand) setArrtxSet('All');
    if (!isSfaihBrand) setSfaihSet('All');
    if (!isCaliartBrand) setCaliartSet('All');
    if (!isBianyoBrand) setBianyoSet('All');
    if (!isHotuBrand) setHotuSet('All');
    if (!isCalmOverChaosBrand) setCalmOverChaosSet('All');
    if (!isHangfanBrand) setHangfanSet('All');
    if (!isSanjokiBrand) setSanjokiSet('All');
    if (!isPrismBrand) setPrismSet('All');
    if (!isRoyalLangnickelBrand) setRoyalLangnickelSet('All');
    if (!isCopicSketchBrand) setCopicSketchSet('All');
    if (!isCopicClassicBrand) setCopicClassicSet('All');
    if (!isCopicCiaoBrand) setCopicCiaoSet('All');
    if (!isMyCollection) setCollectionBrand('All');

    // Only regenerate if brand actually changed or this is initial load without URL code
    if (brandChanged || !initialLoadRef.current) {
      regeneratePalette({
        brand,
        ohuhuSet: isOhuhuBrand ? ohuhuSet : null,
        arrtxSet: isArrtxBrand ? arrtxSet : null,
        sfaihSet: isSfaihBrand ? sfaihSet : null,
        caliartSet: isCaliartBrand ? caliartSet : null,
        bianyoSet: isBianyoBrand ? bianyoSet : null,
        hotuSet: isHotuBrand ? hotuSet : null,
        calmOverChaosSet: isCalmOverChaosBrand ? calmOverChaosSet : null,
        hangfanSet: isHangfanBrand ? hangfanSet : null,
        sanjokiSet: isSanjokiBrand ? sanjokiSet : null,
        prismSet: isPrismBrand ? prismSet : null,
        royalLangnickelSet: isRoyalLangnickelBrand ? royalLangnickelSet : null,
        copicSketchSet: isCopicSketchBrand ? copicSketchSet : null,
        copicClassicSet: isCopicClassicBrand ? copicClassicSet : null,
        copicCiaoSet: isCopicCiaoBrand ? copicCiaoSet : null,
        collectionBrand: isMyCollection ? collectionBrand : null,
      });
    }

    if (brand === "Ohuhu (New)") {
      setOhuhuMode("new");
    } else if (brand === "Ohuhu (Old)") {
      setOhuhuMode("old");
    } else if (brand === "Ohuhu") {
      setOhuhuMode("both");
    }

    // Update collection count when "My Collection" is selected
    if (brand === "My Collection") {
      setCollectionCount(getCollectionMarkerCount());
    }

    cancelReplaceMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand]);

  useEffect(() => {
    if (!isFilterModalOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsFilterModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFilterModalOpen]);

  const handleRandomize = (e) => {

    const flashButton = (el) => {
      if (!el) return;
      if (el.dataset.flashing === "1") return; // avoid stacking
      el.dataset.flashing = "1";
      const prevTransition = el.style.transition || "";
      // use transition for smooth revert
      el.style.transition = "filter 700ms ease, transform 400ms ease, box-shadow 700ms ease";
      // flash state
      el.style.filter = "brightness(1.6)";
      el.style.boxShadow = "0 10px 30px rgba(219,39,119,0.20)";

      // short hold then revert
      setTimeout(() => {
        el.style.filter = "";
        el.style.transform = "";
        el.style.boxShadow = "";
        // cleanup after transition finishes
        setTimeout(() => {
          delete el.dataset.flashing;
          el.style.transition = prevTransition;
        }, 400);
      }, 300);
    };

    // element may be passed as event.currentTarget or fallback to mobile ref
    const el = (e && e.currentTarget) || generateMobileRef.current || null;
    flashButton(el);

    const isOhuhuBrand = brand === "Ohuhu" || brand === "Ohuhu (New)" || brand === "Ohuhu (Old)";
    const isArrtxBrand = brand === "Arrtx";
    const isSfaihBrand = brand === "SFAIH";
    const isCaliartBrand = brand === "Caliart";
    const isBianyoBrand = brand === "Bianyo";
    const isHotuBrand = brand === "Hotu";
    const isCalmOverChaosBrand = brand === "Calm Over Chaos";
    const isHangfanBrand = brand === "Hangfan";
    const isSanjokiBrand = brand === "Sanjoki";
    const isPrismBrand = brand === "Prism";
    const isRoyalLangnickelBrand = brand === "Royal & Langnickel";
    const isCopicSketchBrand = brand === "Copic - Sketch";
    const isCopicClassicBrand = brand === "Copic - Classic";
    const isCopicCiaoBrand = brand === "Copic - Ciao";
    const isMyCollection = brand === "My Collection";
    regeneratePalette({
      ohuhuSet: isOhuhuBrand ? ohuhuSet : null,
      arrtxSet: isArrtxBrand ? arrtxSet : null,
      sfaihSet: isSfaihBrand ? sfaihSet : null,
      caliartSet: isCaliartBrand ? caliartSet : null,
      bianyoSet: isBianyoBrand ? bianyoSet : null,
      hotuSet: isHotuBrand ? hotuSet : null,
      calmOverChaosSet: isCalmOverChaosBrand ? calmOverChaosSet : null,
      hangfanSet: isHangfanBrand ? hangfanSet : null,
      sanjokiSet: isSanjokiBrand ? sanjokiSet : null,
      prismSet: isPrismBrand ? prismSet : null,
      royalLangnickelSet: isRoyalLangnickelBrand ? royalLangnickelSet : null,
      copicSketchSet: isCopicSketchBrand ? copicSketchSet : null,
      copicClassicSet: isCopicClassicBrand ? copicClassicSet : null,
      copicCiaoSet: isCopicCiaoBrand ? copicCiaoSet : null,
      collectionBrand: isMyCollection ? collectionBrand : null,
    });
  };

  const handleSharePalette = () => {
    if (!palette || palette.length === 0) {
      alert(t('randomPalette.errors.noPalette'));
      return;
    }
    const code = encodePaletteForShare(palette);
    setShareCode(code);
    setIsShareOpen(true);
  };

  const handleSavePalette = () => {
    if (!palette || palette.length === 0) {
      alert(t('randomPalette.errors.noPalette'));
      return;
    }
    setIsSaveOpen(true);
  };

  const handleLoadPalette = () => {
    setIsLoadOpen(true);
  };

  const handleLoadPaletteFromModal = (decodedPalette) => {
    loadPalette(decodedPalette);
  };

  const getSetOptions = () => {
    const isOhuhuBrand = brand === "Ohuhu" || brand === "Ohuhu (New)" || brand === "Ohuhu (Old)";
    const isArrtxBrand = brand === "Arrtx";
    const isSfaihBrand = brand === "SFAIH";
    const isCaliartBrand = brand === "Caliart";
    const isBianyoBrand = brand === "Bianyo";
    const isHotuBrand = brand === "Hotu";
    const isCalmOverChaosBrand = brand === "Calm Over Chaos";
    const isHangfanBrand = brand === "Hangfan";
    const isSanjokiBrand = brand === "Sanjoki";
    const isPrismBrand = brand === "Prism";
    const isRoyalLangnickelBrand = brand === "Royal & Langnickel";
    const isCopicSketchBrand = brand === "Copic - Sketch";
    const isCopicClassicBrand = brand === "Copic - Classic";
    const isCopicCiaoBrand = brand === "Copic - Ciao";
    const isMyCollection = brand === "My Collection";
    return {
      ohuhuSet: isOhuhuBrand ? ohuhuSet : null,
      arrtxSet: isArrtxBrand ? arrtxSet : null,
      sfaihSet: isSfaihBrand ? sfaihSet : null,
      caliartSet: isCaliartBrand ? caliartSet : null,
      bianyoSet: isBianyoBrand ? bianyoSet : null,
      hotuSet: isHotuBrand ? hotuSet : null,
      calmOverChaosSet: isCalmOverChaosBrand ? calmOverChaosSet : null,
      hangfanSet: isHangfanBrand ? hangfanSet : null,
      sanjokiSet: isSanjokiBrand ? sanjokiSet : null,
      prismSet: isPrismBrand ? prismSet : null,
      royalLangnickelSet: isRoyalLangnickelBrand ? royalLangnickelSet : null,
      copicSketchSet: isCopicSketchBrand ? copicSketchSet : null,
      copicClassicSet: isCopicClassicBrand ? copicClassicSet : null,
      copicCiaoSet: isCopicCiaoBrand ? copicCiaoSet : null,
      collectionBrand: isMyCollection ? collectionBrand : null,
    };
  };

  const handleFilterSelect = (newFilter, { closeModal = false } = {}) => {
    setFilterAndRegenerate(newFilter, brand, getSetOptions());
    if (closeModal) setIsFilterModalOpen(false);
  };

  const handleSimilarSubfilterSelect = (newSubfilter, { closeModal = false } = {}) => {
    setSimilarSubfilterAndRegenerate(newSubfilter, brand, getSetOptions());
    if (closeModal) setIsFilterModalOpen(false);
  };

  const startReplaceFlow = () => {
    setReplacementColor(null);
    setIsReplaceMode(false);
    setIsSearchModalOpen(true);
  };

  const handleMarkerPicked = (marker) => {
    setReplacementColor(marker);
    setIsSearchModalOpen(false);
    setIsReplaceMode(true);
  };

  function cancelReplaceMode() {
    setIsReplaceMode(false);
    setReplacementColor(null);
  }

  const handleReplaceAtIndex = (idx) => {
    if (!replacementColor) return;
    replaceColorAt(idx, replacementColor);
    setIsReplaceMode(false);
    setReplacementColor(null);
  };

  useEffect(() => {
    if (!isReplaceMode) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        cancelReplaceMode();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isReplaceMode]);

  return (
    <div className="container relative mx-auto">
      {/* Mobile Floating Action Buttons */}
      <div className="fixed bottom-6 right-4 z-30 flex flex-col gap-3 lg:hidden">
        <button
          onClick={startReplaceFlow}
          className="w-14 h-14 bg-gradient-to-r from-indigo-400 to-cyan-400 text-white rounded-full shadow-lg flex items-center justify-center text-xl transition-all transform hover:scale-105"
          title={t('randomPalette.search.cta')}
        >
          <Search size={22} />
        </button>
        <button
          onClick={() => setIsFilterModalOpen(true)}
          className="w-14 h-14 bg-gradient-to-r from-sky-400 to-emerald-400 text-white rounded-full shadow-lg flex items-center justify-center text-xl transition-all transform hover:scale-105"
          title={t('filters.title')}
        >
          <Palette size={24} />
        </button>
        <button
          ref={generateMobileRef}
          onClick={handleRandomize}
          className="w-14 h-14 bg-gradient-to-r from-pink-400 to-rose-pastel-600 text-white rounded-full shadow-lg flex items-center justify-center text-2xl transition-all transform"
          title={t('randomPalette.generate')}
        >
          <Dices size={24} />
        </button>
      </div>

      {/* Welcome Section */}
      <div className="text-center mb-4">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-pink-400 to-rose-500">
          {t('randomPalette.title')}
          <Dices size={32} className="inline-block ml-2 text-rose-500 pb-1" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('randomPalette.subtitle')}
        </p>
        <PageHelpModal
          title={t('randomPalette.help.title')}
          tips={[
            { text: t('randomPalette.help.tip1') },
            { text: t('randomPalette.help.tip2') },
            { text: t('randomPalette.help.tip3Mobile'), mobileOnly: true },
            { text: t('randomPalette.help.tip3Desktop'), desktopOnly: true },
            { text: t('randomPalette.help.tip4Mobile'), mobileOnly: true },
            { text: t('randomPalette.help.tip4Desktop'), desktopOnly: true },
            { text: t('randomPalette.help.tip5') },
            { text: t('randomPalette.help.tip6') },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              {t('common.needHelp')}
            </p>
          }
        />
      </div>

      {/* My Collection Banner — only shown when collection is empty */}
      {brand === "My Collection" && collectionCount === 0 && (
        <div className="mb-6 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-4 shadow-soft">
            <div className="flex items-center gap-3">
              <FolderHeart size={24} className="text-yellow-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-slate-700 font-semibold">
                  {t('brandSelector.myCollection')} - {t('randomPalette.myCollection.empty')}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {t('randomPalette.myCollection.emptyHint')}
                </p>
              </div>
              <button
                onClick={() => navigate('/collection')}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold text-sm transition-colors shadow-sm flex-shrink-0"
              >
                {t('randomPalette.myCollection.manage')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Always-visible controls */}
      <div className="lg:hidden mb-6">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[150px] space-y-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 block ml-1">
              {t('brandSelector.aria')}
            </span>
            <BrandSelector value={brand} onChange={setBrand} />
            {(brand === "Ohuhu" || brand === "Ohuhu (New)" || brand === "Ohuhu (Old)") && (
              <SetSelect value={ohuhuSet} legend={OHUHU_SET_LEGEND} ariaLabel="Ohuhu Set" onChange={v => { setOhuhuSet(v); regeneratePalette({ brand, ...getSetOptions(), ohuhuSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Arrtx" && (
              <SetSelect value={arrtxSet} legend={ARRTX_SET_LEGEND} ariaLabel="Arrtx Set" onChange={v => { setArrtxSet(v); regeneratePalette({ brand, ...getSetOptions(), arrtxSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "SFAIH" && (
              <SetSelect value={sfaihSet} legend={SFAIH_SET_LEGEND} ariaLabel="SFAIH Set" onChange={v => { setSfaihSet(v); regeneratePalette({ brand, ...getSetOptions(), sfaihSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Caliart" && (
              <SetSelect value={caliartSet} legend={CALIART_SETS} ariaLabel="Caliart Set" onChange={v => { setCaliartSet(v); regeneratePalette({ brand, ...getSetOptions(), caliartSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Bianyo" && (
              <SetSelect value={bianyoSet} legend={BIANYO_SETS} ariaLabel="Bianyo Set" onChange={v => { setBianyoSet(v); regeneratePalette({ brand, ...getSetOptions(), bianyoSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Hotu" && (
              <SetSelect value={hotuSet} legend={HOTU_SETS} ariaLabel="Hotu Set" onChange={v => { setHotuSet(v); regeneratePalette({ brand, ...getSetOptions(), hotuSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Calm Over Chaos" && (
              <SetSelect value={calmOverChaosSet} legend={CALM_OVER_CHAOS_SETS} ariaLabel="Calm Over Chaos Set" onChange={v => { setCalmOverChaosSet(v); regeneratePalette({ brand, ...getSetOptions(), calmOverChaosSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Hangfan" && (
              <SetSelect value={hangfanSet} legend={HANGFAN_SETS} ariaLabel="Hangfan Set" onChange={v => { setHangfanSet(v); regeneratePalette({ brand, ...getSetOptions(), hangfanSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Sanjoki" && (
              <SetSelect value={sanjokiSet} legend={SANJOKI_SET_LEGEND} ariaLabel="Sanjoki Set" onChange={v => { setSanjokiSet(v); regeneratePalette({ brand, ...getSetOptions(), sanjokiSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Prism" && (
              <SetSelect value={prismSet} legend={PRISM_SETS} ariaLabel="Prism Set" onChange={v => { setPrismSet(v); regeneratePalette({ brand, ...getSetOptions(), prismSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Royal & Langnickel" && (
              <SetSelect value={royalLangnickelSet} legend={ROYAL_LANGNICKEL_SETS} ariaLabel="Royal & Langnickel Set" onChange={v => { setRoyalLangnickelSet(v); regeneratePalette({ brand, ...getSetOptions(), royalLangnickelSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Copic - Sketch" && (
              <SetSelect value={copicSketchSet} legend={COPIC_SKETCH_SETS} ariaLabel="Copic Sketch Set" onChange={v => { setCopicSketchSet(v); regeneratePalette({ brand, ...getSetOptions(), copicSketchSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Copic - Classic" && (
              <SetSelect value={copicClassicSet} legend={COPIC_CLASSIC_SETS} ariaLabel="Copic Classic Set" onChange={v => { setCopicClassicSet(v); regeneratePalette({ brand, ...getSetOptions(), copicClassicSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "Copic - Ciao" && (
              <SetSelect value={copicCiaoSet} legend={COPIC_CIAO_SETS} ariaLabel="Copic Ciao Set" onChange={v => { setCopicCiaoSet(v); regeneratePalette({ brand, ...getSetOptions(), copicCiaoSet: v === 'All' ? null : v }); }} />
            )}
            {brand === "My Collection" && (() => {
              const collectionBrands = [...new Set(getUserCollection().map(m => m.brand))].sort();
              if (collectionBrands.length < 2) return null;
              return (
                <div className="relative mt-1.5">
                  <select
                    value={collectionBrand}
                    onChange={e => { setCollectionBrand(e.target.value); regeneratePalette({ brand, ...getSetOptions(), collectionBrand: e.target.value === 'All' ? null : e.target.value }); }}
                    className="w-full appearance-none pr-8 text-xs py-2 px-3 cursor-pointer rounded-xl bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
                    aria-label="Collection Brand"
                  >
                    <option value="All">All Markers</option>
                    {collectionBrands.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-3 h-3 text-rose-pastel-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="flex flex-col gap-1.5 items-end ml-auto">
            <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              {t('randomPalette.count.label')}
            </span>
            <CountSelect value={count} onChange={setCountClamped} />
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(280px,320px)_1fr] lg:items-start">
        {/* Desktop Filter Sidebar */}
        <div className="hidden lg:block pt-[22px]">
          <FilterSidebar
            active={filter} 
            onChange={handleFilterSelect}
            similarSubfilter={similarSubfilter}
            onSimilarSubfilterChange={handleSimilarSubfilterSelect}
            isSimilarMode={isSimilarMode}
          />
        </div>

        {/* Main Content */}
        <section className="grid gap-8">
          {/* Desktop Controls */}
          <div className="hidden lg:block">
            <div className="grid md:grid-cols-[minmax(240px,1fr)_auto_auto] gap-6 items-start">
              <div className="space-y-1.5">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 ml-1">
                  {t('brandSelector.aria')}
                </span>
                <BrandSelector
                  value={brand}
                  onChange={setBrand}
                />
                {(brand === "Ohuhu" || brand === "Ohuhu (New)" || brand === "Ohuhu (Old)") && (
                  <SetSelect value={ohuhuSet} legend={OHUHU_SET_LEGEND} ariaLabel="Ohuhu Set" onChange={v => { setOhuhuSet(v); regeneratePalette({ brand, ...getSetOptions(), ohuhuSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Arrtx" && (
                  <SetSelect value={arrtxSet} legend={ARRTX_SET_LEGEND} ariaLabel="Arrtx Set" onChange={v => { setArrtxSet(v); regeneratePalette({ brand, ...getSetOptions(), arrtxSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "SFAIH" && (
                  <SetSelect value={sfaihSet} legend={SFAIH_SET_LEGEND} ariaLabel="SFAIH Set" onChange={v => { setSfaihSet(v); regeneratePalette({ brand, ...getSetOptions(), sfaihSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Caliart" && (
                  <SetSelect value={caliartSet} legend={CALIART_SETS} ariaLabel="Caliart Set" onChange={v => { setCaliartSet(v); regeneratePalette({ brand, ...getSetOptions(), caliartSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Bianyo" && (
                  <SetSelect value={bianyoSet} legend={BIANYO_SETS} ariaLabel="Bianyo Set" onChange={v => { setBianyoSet(v); regeneratePalette({ brand, ...getSetOptions(), bianyoSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Hotu" && (
                  <SetSelect value={hotuSet} legend={HOTU_SETS} ariaLabel="Hotu Set" onChange={v => { setHotuSet(v); regeneratePalette({ brand, ...getSetOptions(), hotuSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Calm Over Chaos" && (
                  <SetSelect value={calmOverChaosSet} legend={CALM_OVER_CHAOS_SETS} ariaLabel="Calm Over Chaos Set" onChange={v => { setCalmOverChaosSet(v); regeneratePalette({ brand, ...getSetOptions(), calmOverChaosSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Hangfan" && (
                  <SetSelect value={hangfanSet} legend={HANGFAN_SETS} ariaLabel="Hangfan Set" onChange={v => { setHangfanSet(v); regeneratePalette({ brand, ...getSetOptions(), hangfanSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Sanjoki" && (
                  <SetSelect value={sanjokiSet} legend={SANJOKI_SET_LEGEND} ariaLabel="Sanjoki Set" onChange={v => { setSanjokiSet(v); regeneratePalette({ brand, ...getSetOptions(), sanjokiSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Prism" && (
                  <SetSelect value={prismSet} legend={PRISM_SETS} ariaLabel="Prism Set" onChange={v => { setPrismSet(v); regeneratePalette({ brand, ...getSetOptions(), prismSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Royal & Langnickel" && (
                  <SetSelect value={royalLangnickelSet} legend={ROYAL_LANGNICKEL_SETS} ariaLabel="Royal & Langnickel Set" onChange={v => { setRoyalLangnickelSet(v); regeneratePalette({ brand, ...getSetOptions(), royalLangnickelSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Copic - Sketch" && (
                  <SetSelect value={copicSketchSet} legend={COPIC_SKETCH_SETS} ariaLabel="Copic Sketch Set" onChange={v => { setCopicSketchSet(v); regeneratePalette({ brand, ...getSetOptions(), copicSketchSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Copic - Classic" && (
                  <SetSelect value={copicClassicSet} legend={COPIC_CLASSIC_SETS} ariaLabel="Copic Classic Set" onChange={v => { setCopicClassicSet(v); regeneratePalette({ brand, ...getSetOptions(), copicClassicSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "Copic - Ciao" && (
                  <SetSelect value={copicCiaoSet} legend={COPIC_CIAO_SETS} ariaLabel="Copic Ciao Set" onChange={v => { setCopicCiaoSet(v); regeneratePalette({ brand, ...getSetOptions(), copicCiaoSet: v === 'All' ? null : v }); }} />
                )}
                {brand === "My Collection" && (() => {
                  const collectionBrands = [...new Set(getUserCollection().map(m => m.brand))].sort();
                  if (collectionBrands.length < 2) return null;
                  return (
                    <div className="relative mt-1.5">
                      <select
                        value={collectionBrand}
                        onChange={e => { setCollectionBrand(e.target.value); regeneratePalette({ brand, ...getSetOptions(), collectionBrand: e.target.value === 'All' ? null : e.target.value }); }}
                        className="w-full appearance-none pr-8 text-xs py-2 px-3 cursor-pointer rounded-xl bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
                        aria-label="Collection Brand"
                      >
                        <option value="All">All Markers</option>
                        {collectionBrands.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-3 h-3 text-rose-pastel-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="space-y-1.5 justify-self-end">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 text-right">
                  {t('randomPalette.count.label')}
                </span>
                <CountSelect value={count} onChange={setCountClamped} />
              </div>

              <div className="flex items-center justify-end justify-self-end pt-6 min-w-[160px] w-full">
                <button
                  className="btn btn-primary-green text-lg flex items-center gap-2 justify-center w-full"
                  onClick={handleRandomize}
                >
                  {t('randomPalette.generate')}
                </button>
              </div>
            </div>
          </div>

          {/* Palette Display */}
          <div className="space-y-6" ref={paletteRef}>
            {/* <div className="text-center w-full">
              
              <p className="text-sm text-elegant mb-4">
                {filter === 'Similar' && similarSubfilter !== 'All Hues' && ` (${similarSubfilter})`}
              </p>
            </div> */}

            {/* Navigation & Layout */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Navigation */}
              <div className="flex items-center gap-3">
                <button
                  className={`p-1.5 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:ring-offset-2 ${hasPreviousPalette() ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-100' : 'text-slate-300 cursor-not-allowed'}`}
                  onClick={hasPreviousPalette() ? goToPreviousPalette : undefined}
                  disabled={!hasPreviousPalette()}
                  title={hasPreviousPalette() ? t('randomPalette.nav.prevTitle') : t('randomPalette.nav.prevDisabled')}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  className={`p-1.5 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:ring-offset-2 ${hasNextPalette() ? 'text-slate-500 hover:text-slate-700 hover:bg-slate-100' : 'text-slate-300 cursor-not-allowed'}`}
                  onClick={hasNextPalette() ? goToNextPalette : undefined}
                  disabled={!hasNextPalette()}
                  title={hasNextPalette() ? t('randomPalette.nav.nextTitle') : t('randomPalette.nav.nextDisabled')}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              {/* <h3 className="text-xl font-display text-slate-600 tracking-[0.08em]">
                {t('randomPalette.generated.title')}
              </h3> */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 min-w-[80px]"
                  onClick={() => setIsCompactMode((prev) => !prev)}
                  aria-pressed={isCompactMode}
                  aria-label={t('randomPalette.view.aria')}
                >
                  {isCompactMode ? <List size={13} /> : <LayoutGrid size={13} />}
                  <span>
                    {isCompactMode
                      ? t('randomPalette.view.list')
                      : t('randomPalette.view.compact')}
                  </span>
                </button>
                <button
                  type="button"
                  className="hidden lg:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all shadow-soft bg-white/90 text-slate-600 hover:bg-rose-pastel-100"
                  onClick={startReplaceFlow}
                  title={t('randomPalette.search.cta')}
                >
                  <Search size={16} />
                  <span>{t('randomPalette.search.cta')}</span>
                </button>
              </div>
            </div>
            
            {/* Debug / empty */}
            {palette.length === 0 && (
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-yellow-800">{t('randomPalette.empty')}</p>
              </div>
            )}

            {/* {(palette.length !== count) && (
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-yellow-800">
                  <p>You tried to generate {count} colors, but only {palette.length} could be found.</p>
                  <p>This might be due to filter constraints. Try adjusting your filters or increasing the color count.</p>
                </div>
              </div>
            )} */}
            {isReplaceMode && (
              <div className="flex flex-wrap items-center justify-between gap-3 bg-rose-pastel-50 border border-rose-pastel-200 rounded-xl px-4 py-3 text-sm text-rose-pastel-700">
                <span className="font-semibold">{t('randomPalette.search.replacePrompt')}</span>
                <button
                  type="button"
                  className="btn btn-secondary text-xs px-3 py-1"
                  onClick={cancelReplaceMode}
                >
                  {t('randomPalette.search.cancel')}
                </button>
              </div>
            )}
            <div>
              <PaletteGrid
                palette={palette}
                locks={locks}
                onToggleLock={toggleLock}
                ohuhuMode={ohuhuMode}
                compactMode={isCompactMode}
                mobileCompactMode={false}
                replaceMode={isReplaceMode}
                onReplaceSelect={handleReplaceAtIndex}
              />
            </div>
            
            {/* <div className="text-center">
              
              <span>
                {t('randomPalette.generated.tip')}
              </span>
              <br/>
              <span className="text-sm text-slate-400">
                <TriangleAlert size={20} className="inline-block mr-1 text-slate-300" />
                {t('randomPalette.generated.warning')}
              </span>
            </div> */}

            {/* Desktop and Mobile Bottom Toolbar */}
            <div className="flex justify-center lg:justify-end gap-3 mt-5">
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white text-xs font-semibold shadow-md transition-all"
                onClick={handleLoadPalette}
              >
                <Download size={14} />
                <span>Import</span>
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-rose-pastel-300 to-pink-300 hover:from-rose-pastel-400 hover:to-pink-400 text-white text-xs font-semibold shadow-md transition-all"
                onClick={handleSavePalette}
              >
                <Heart size={14} />
                <span>Save</span>
              </button>
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-400 to-purple-500 hover:from-indigo-500 hover:to-purple-600 text-white text-xs font-semibold shadow-md transition-all"
                onClick={handleSharePalette}
              >
                <Share size={14} />
                <span>Share</span>
              </button>
            </div>

      </div>
    </section>
  </div>

      {isFilterModalOpen && (
        <div className="fixed inset-0 z-40 backdrop-blur-sm flex items-end md:items-center justify-center p-4 animate-fadeIn duration-300 lg:hidden">
          <div
            className="absolute inset-0 z-0"
            onClick={() => setIsFilterModalOpen(false)}
          />
          <div className="relative w-full px-4 pb-6 z-10">
            <div className="relative">
              <FilterSidebar
                active={filter}
                onChange={(newFilter) => handleFilterSelect(newFilter, { closeModal: true })}
                similarSubfilter={similarSubfilter}
                onSimilarSubfilterChange={(newSubfilter) =>
                  handleSimilarSubfilterSelect(newSubfilter, { closeModal: true })
                }
                isSimilarMode={isSimilarMode}
              />
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(false)}
                className="absolute top-3 right-3 w-10 h-10 text-slate-600 hover:text-slate-800 flex items-center justify-center"
                aria-label={t('sharePaletteModal.closeAria')}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      <MarkerSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        brand={brand}
        ohuhuMode={ohuhuMode}
        onSelectMarker={handleMarkerPicked}
      />

      <SharePaletteModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        code={shareCode}
        palette={palette}
        ohuhuMode={ohuhuMode}
        paletteRef={paletteRef}
      />

      <SavePaletteModal
        isOpen={isSaveOpen}
        onClose={() => setIsSaveOpen(false)}
        palette={palette}
      />

      <LoadPaletteModal
        isOpen={isLoadOpen}
        onClose={() => setIsLoadOpen(false)}
        onLoadPalette={handleLoadPaletteFromModal}
        onSaveRequest={(markers) => { setImportMarkers(markers); }}
      />

      <ImportPaletteModal
        isOpen={!!importMarkers}
        onClose={() => setImportMarkers(null)}
        preloadedMarkers={importMarkers}
        onLoadInCreator={(markers) => {
          loadPalette(markers);
          setImportMarkers(null);
        }}
      />
    </div>
  );
}
